import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from './config/apiConfig';
import { buildOrderDetailLangQuery } from './utils/buildOrderDetailLangQuery';
import OrderDesign from './OrderDesign';
import { RankInsigniaVertical } from './pvp/RankInsigniaVertical';
import { requestPendingSeasonEndCheck } from './utils/requestPendingSeasonEndCheck';
import { getLocalizedSeasonName } from './utils/honorHallSeasonLocalization';
import './WelcomeExploreUnknownLeaderboard.css';
import './WelcomeLeftStack.css';

const DEV_SEASON_ACTIONS = Object.freeze({
  start: '/honor-hall/dev-season/start',
  thirtyOneMinutes: '/honor-hall/dev-season/thirty-one-minutes-before-end',
  sixteenMinutes: '/honor-hall/dev-season/sixteen-minutes-before-end',
  fifteenMinutes: '/honor-hall/dev-season/fifteen-minutes-before-end',
  ninetySeconds: '/honor-hall/dev-season/ninety-seconds-before-end',
  finish: '/honor-hall/dev-season/finish',
});

const POLL_MS = 30000;

const I18N_LOCALE_BY_LANG = Object.freeze({
  'en-us': 'en-US',
  'de-de': 'de-DE',
  'es-es': 'es-ES',
});

function formatSeasonEndForLocale(endUtcIso, language) {
  if (!endUtcIso) {
    throw new Error('WelcomeSeasonPanel: seasonEndUtc is required for display');
  }
  const endDate = new Date(endUtcIso);
  const locale = I18N_LOCALE_BY_LANG[language] || language || 'en-US';
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  }).format(endDate);
}

function formatRank(rank) {
  if (rank == null || Number.isNaN(Number(rank))) {
    return '—';
  }
  return `#${rank}`;
}

/**
 * @param {{ isMobile?: boolean, isDevPlayer1?: boolean }} props
 */
function WelcomeSeasonPanel({ isMobile = false, isDevPlayer1 = false }) {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [rewardOrder, setRewardOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [hoverReward, setHoverReward] = useState(false);
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const rewardThumbRef = useRef(null);
  const [devLoading, setDevLoading] = useState(false);
  const [devError, setDevError] = useState(null);

  const loadSummary = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setFetchFailed(true);
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/honor-hall/season/welcome-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`welcome-summary HTTP ${response.status}`);
      }
      const data = await response.json();
      setSummary(data);
      setFetchFailed(false);
      setLoading(false);

      if (data.rewardOrderId) {
        const langQuery = buildOrderDetailLangQuery(i18n.language);
        const orderRes = await fetch(`${API_URL}/order/${data.rewardOrderId}/full?${langQuery}`);
        if (orderRes.ok) {
          const orderData = await orderRes.json();
          setRewardOrder(orderData);
        }
      }
    } catch (err) {
      console.error('WelcomeSeasonPanel: fetch failed', err);
      setFetchFailed(true);
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    loadSummary();
    const id = setInterval(loadSummary, POLL_MS);
    return () => clearInterval(id);
  }, [loadSummary]);

  const handleDevSeasonAction = async (path) => {
    setDevLoading(true);
    setDevError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Missing auth token');
      }
      const response = await fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${response.status}`);
      }
      const payload = await response.json();
      await loadSummary();
      if (typeof window === 'undefined') {
        throw new Error('WelcomeSeasonPanel: window is required after dev season action');
      }
      // Time-jumps arm timers only (live wait). Do not flash the current warning tier.
      // Finish creates pending rows, then pending-end discovery opens the dialog.
      const statusDetail = payload.status
        ? {
            ...payload.status,
            armTimersOnly: path !== DEV_SEASON_ACTIONS.start && path !== DEV_SEASON_ACTIONS.finish,
          }
        : null;
      window.dispatchEvent(
        new CustomEvent('coda-dev-season-changed', { detail: statusDetail })
      );
      if (path === DEV_SEASON_ACTIONS.finish) {
        requestPendingSeasonEndCheck();
      }
    } catch (err) {
      console.error('WelcomeSeasonPanel: dev season action failed', err);
      setDevError(err.message || 'Dev season action failed');
    } finally {
      setDevLoading(false);
    }
  };

  const ranks = summary?.ranks || {};

  const updateHoverAnchor = useCallback(() => {
    if (!rewardThumbRef.current) {
      throw new Error('WelcomeSeasonPanel: reward thumb ref missing during hover');
    }
    const rect = rewardThumbRef.current.getBoundingClientRect();
    setHoverAnchor({
      left: rect.right + 12,
      top: rect.top + rect.height / 2,
    });
  }, []);

  const handleRewardMouseEnter = () => {
    setHoverReward(true);
    updateHoverAnchor();
  };

  const handleRewardMouseLeave = () => {
    setHoverReward(false);
    setHoverAnchor(null);
  };

  return (
    <aside
      className={`welcome-eu-leaderboard welcome-season-panel${isMobile ? ' welcome-eu-leaderboard--mobile' : ''}`}
      aria-label={t('welcome.seasonPanel.ariaLabel')}
    >
      <div className="welcome-eu-leaderboard__safe-area">
        <span className="welcome-eu-leaderboard-corner welcome-eu-leaderboard-corner--tr" aria-hidden="true" />
        <span className="welcome-eu-leaderboard-corner welcome-eu-leaderboard-corner--br" aria-hidden="true" />
        <div className="welcome-eu-leaderboard-header">
          <div className="welcome-season-panel-insignia" aria-hidden="true">
            <div className="welcome-season-panel-insignia__scale">
              <RankInsigniaVertical rankKey="general" />
            </div>
          </div>
          <h2 className="welcome-eu-leaderboard-title">{t('welcome.seasonPanel.title')}</h2>
        </div>

        {isDevPlayer1 && (
          <div className="welcome-honor-movements-dev welcome-season-panel-dev">
            <button
              type="button"
              className="welcome-honor-movements-dev-btn"
              onClick={() => handleDevSeasonAction(DEV_SEASON_ACTIONS.start)}
              disabled={devLoading}
            >
              {devLoading ? t('welcome.seasonPanel.devLoading') : t('welcome.seasonPanel.devStartSeason')}
            </button>
            <button
              type="button"
              className="welcome-honor-movements-dev-btn"
              onClick={() => handleDevSeasonAction(DEV_SEASON_ACTIONS.thirtyOneMinutes)}
              disabled={devLoading}
            >
              {t('welcome.seasonPanel.devThirtyOneMinutesBeforeEnd')}
            </button>
            <button
              type="button"
              className="welcome-honor-movements-dev-btn"
              onClick={() => handleDevSeasonAction(DEV_SEASON_ACTIONS.sixteenMinutes)}
              disabled={devLoading}
            >
              {t('welcome.seasonPanel.devSixteenMinutesBeforeEnd')}
            </button>
            <button
              type="button"
              className="welcome-honor-movements-dev-btn"
              onClick={() => handleDevSeasonAction(DEV_SEASON_ACTIONS.fifteenMinutes)}
              disabled={devLoading}
            >
              {t('welcome.seasonPanel.devFifteenMinutesBeforeEnd')}
            </button>
            <button
              type="button"
              className="welcome-honor-movements-dev-btn"
              onClick={() => handleDevSeasonAction(DEV_SEASON_ACTIONS.ninetySeconds)}
              disabled={devLoading}
            >
              {t('welcome.seasonPanel.devNinetySecondsBeforeEnd')}
            </button>
            <button
              type="button"
              className="welcome-honor-movements-dev-btn"
              onClick={() => handleDevSeasonAction(DEV_SEASON_ACTIONS.finish)}
              disabled={devLoading}
            >
              {t('welcome.seasonPanel.devFinishSeason')}
            </button>
            {devError && (
              <p className="welcome-eu-leaderboard-status welcome-eu-leaderboard-status--error">
                {devError}
              </p>
            )}
          </div>
        )}

        {loading && !summary && !fetchFailed && (
          <p className="welcome-eu-leaderboard-status">{t('welcome.seasonPanel.loading')}</p>
        )}

        {fetchFailed && !summary && (
          <p className="welcome-eu-leaderboard-status welcome-eu-leaderboard-status--error">
            {t('welcome.seasonPanel.loadFailed')}
          </p>
        )}

        {summary?.active === false && (
          <p className="welcome-eu-leaderboard-status">{t('welcome.seasonPanel.notStarted')}</p>
        )}

        {summary?.active !== false && summary && (
          <div className="welcome-season-panel-body">
            <p className="welcome-season-panel-heading">
              {t('welcome.seasonPanel.seasonLine', {
                number: summary.seasonNumber,
                name: getLocalizedSeasonName(t, summary.seasonNumber),
              })}
            </p>
            <p className="welcome-season-panel-end">
              {t('welcome.seasonPanel.endsAt', {
                datetime: formatSeasonEndForLocale(summary.seasonEndUtc, i18n.language),
              })}
            </p>
            <ul className="welcome-season-panel-ranks">
              <li>{t('welcome.seasonPanel.battleLegends', { rank: formatRank(ranks.battleLegends) })}</li>
              <li>{t('welcome.seasonPanel.battleStrategies', { rank: formatRank(ranks.battleStrategies) })}</li>
              <li>{t('welcome.seasonPanel.warHeroes', { rank: formatRank(ranks.warHeroes) })}</li>
              <li>{t('welcome.seasonPanel.explorers', { rank: formatRank(ranks.explorers) })}</li>
              <li>{t('welcome.seasonPanel.assetStrategies', { rank: formatRank(ranks.assetStrategies) })}</li>
            </ul>
            <div className="welcome-season-panel-reward">
              <span className="welcome-season-panel-reward-label">
                {t('welcome.seasonPanel.rewardLabel', { copies: summary.projectedRewardCopies || 0 })}
              </span>
              {rewardOrder && (
                <div
                  className="welcome-season-panel-reward-card"
                  onMouseEnter={handleRewardMouseEnter}
                  onMouseLeave={handleRewardMouseLeave}
                >
                  <div className="welcome-season-panel-reward-thumb" ref={rewardThumbRef}>
                    <div className="welcome-season-panel-reward-thumb__inner">
                      <OrderDesign
                        order={rewardOrder}
                        count={0}
                        minimal
                        mediaOnly
                        lazyLoad={false}
                        isOOR={false}
                        noMargin
                      />
                    </div>
                  </div>
                  {hoverReward && hoverAnchor && typeof document !== 'undefined'
                    ? createPortal(
                        <div
                          className="welcome-season-panel-reward-hover"
                          style={{
                            position: 'fixed',
                          left: `${hoverAnchor.left}px`,
                          top: `${hoverAnchor.top}px`,
                          transform: 'translateY(-50%)',
                          zIndex: 99999,
                          pointerEvents: 'none',
                        }}
                        >
                          <OrderDesign
                            order={rewardOrder}
                            count={0}
                            minimal={false}
                            noMargin
                            lazyLoad={false}
                            isOOR={false}
                          />
                        </div>,
                        document.body
                      )
                    : null}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

export default WelcomeSeasonPanel;
